import React, { useEffect } from "react";
import { Shield } from "lucide-react";
import { useScan } from "../contexts/ScanContext";
import { Link, useLocation, useNavigate } from "react-router-dom";
import securityApi from "../api/securityApi";
import { GUEST_SCANS_ENABLED } from "../utils/guestScans";

const LoginPage = () => {
  const API_BASE_URL =
    process.env.REACT_APP_API_URL || "http://localhost:3001/api";
  const { handleLoginSuccess, user } = useScan();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate("/welcome");
    }
  }, [user, navigate]);

  useEffect(() => {
    // Check for success parameter in URL
    const params = new URLSearchParams(location.search);
    if (params.get('success') === 'true') {
      // Fetch user data after successful login
      const fetchUser = async () => {
        try {
          const userData = await securityApi.getCurrentUser();
          handleLoginSuccess(userData);
          navigate("/welcome");
        } catch (error) {
          console.error('Failed to fetch user data:', error);
        }
      };
      fetchUser();
    }
  }, [location, handleLoginSuccess, navigate]);

  const handleGoogleLogin = () => {
    securityApi.loginWithGoogle();
  };

  const handleGitHubLogin = () => {
    // This would be implemented when GitHub auth is ready
    securityApi.loginWithGithub();
    // console.log('GitHub login clicked');
  };

  const handleEmailSignup = () => {
    // This would be implemented when email auth is ready
    console.log('Email signup clicked');
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white border border-gray-200 rounded-xl p-8 shadow-lg">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <Shield className="h-16 w-16 text-teal-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Welcome to Fenny</h1>
          <p className="text-gray-600 mt-2">Your First AI Product Security Hire</p>
        </div>

        <div className="space-y-4">
          <button
            onClick={handleGitHubLogin}
            className="flex items-center justify-center w-full bg-gray-900 text-white py-3 px-4 rounded-lg font-medium hover:bg-gray-800 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
            </svg>
            Continue with GitHub
          </button>

          <button
            onClick={handleGoogleLogin}
            className="flex items-center justify-center w-full bg-white border border-gray-300 text-gray-700 py-3 px-4 rounded-lg font-medium hover:bg-gray-50 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            Continue with Google
          </button>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-200"></div>
            </div>
            {/* <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or</span>
            </div> */}
          </div>

          {GUEST_SCANS_ENABLED && (
            <Link
              to="/scan?method=direct"
              className="block w-full text-center text-teal-600 hover:text-teal-700 py-2 font-medium"
            >
              Scan a public repository without signing in
            </Link>
          )}

          {/* <button
            onClick={handleEmailSignup}
            className="w-full bg-teal-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-teal-700 transition-colors"
          >
            Sign up with Email
          </button> */}
        </div>

        <div className="mt-6 text-center text-sm text-gray-500">
          <p>
            By signing up, you agree to our{' '}
            <a href="/terms-of-service" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700">
              Terms of Service
            </a>{' '}
            and{' '}
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700">
              Privacy Policy
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
